/**
 * Sidebar/config.ts
 * desc: declarative structure of navigation v2 — the daily-work items, the Setup groups, and the bottom menu
 * note: an item only names its app-urls key; whether the user may see it is derived from that route's
 *       auth block in routes.ts (see useNavAccess), so the sidebar can never disagree with the router
 */

import appUrls from '../../settings/app-urls';
import { AnalyticsIcon, BookIcon, DiscoverIcon, GridIcon, HomeIcon, JourneyIcon, ReceiptIcon, ReviewsIcon, SettingsIcon, type Icon } from '../Icon/typed';
import { FILE_STORAGE_FLAG } from '../../modules/FileStorage/constants';
import { WIKI_PAGES_PERMISSIONS } from '../../modules/WikiPages/constants';

export const NAVIGATION_V2_FLAG = 'web.global.navigationV2.enabled';

export type AppUrlKey = keyof typeof appUrls;

export interface NavItem {
    // stable identity, independent of label and path (analytics, tests)
    id: string;
    // destination, and the route whose auth block gates the item
    appUrlKey: AppUrlKey;
    labelKey: string;
    // row icon — daily-work and bottom items have one, Setup children are text-only
    icon?: Icon;
    // LaunchDarkly flag that must be truthy for the item to render
    flag?: string;
    // extra route keys (prefix-matched) that highlight this item — for pages whose URL does not sit under the item's path
    activeOn?: AppUrlKey[];
}

export interface NavGroup {
    id: string;
    labelKey: string;
    items: NavItem[];
}

// a row that opens something in place instead of navigating — gated by its own permissions since it has no route
export interface NavAction {
    id: string;
    labelKey: string;
    icon: Icon;
    flag?: string;
    // the user needs at least one of these
    permissions: string[];
}

// a row that leaves the app — gated like an action (it has no route); the href depends on the tenant's hostname
export interface NavExternalLink extends NavAction {
    getHref: () => string;
}

export const DAILY_ITEMS: NavItem[] = [
    { id: 'home', appUrlKey: 'HOME', labelKey: 'nav.link.home', icon: HomeIcon },
    { id: 'journeys', appUrlKey: 'JOURNEYS', labelKey: 'nav.link.journeys', icon: JourneyIcon },
    { id: 'trips', appUrlKey: 'ANALYTICS_TRIPS', labelKey: 'nav.link.trips', icon: AnalyticsIcon, activeOn: [ 'ANALYTICS' ] },
    { id: 'orders', appUrlKey: 'ORDERS', labelKey: 'nav.link.orders', icon: ReceiptIcon },
    { id: 'reviews', appUrlKey: 'REVIEWS', labelKey: 'nav.link.reviews', icon: ReviewsIcon, activeOn: [ 'ORDER_REVIEWS_LIST' ] },
];

// the external BI dashboard, rendered right after the daily-work items (the legacy Analytics hub carried this link)
export const ANALYTICS_DASHBOARD: NavExternalLink = {
    id: 'analyticsDashboard',
    labelKey: 'nav.link.dashboard',
    icon: GridIcon,
    flag: 'web.analyticsDashboard.feature.enabled',
    permissions: [ 'trips:read', 'trips:write' ],
    getHref: () => `https://analytics-${window.location.hostname.split('.')[0]}.ymeadows.com`,
};

export const SETUP_GROUPS: NavGroup[] = [
    {
        id: 'automation',
        labelKey: 'nav.group.automation',
        items: [
            { id: 'webhookTester', appUrlKey: 'TESTING_WEBHOOK', labelKey: 'nav.link.webhookTester', flag: 'web.testing.feature.enabled' },
            { id: 'stepFactories', appUrlKey: 'COMPONENTS_STEP_FACTORIES', labelKey: 'nav.link.stepFactories' },
        ],
    },
    {
        id: 'orders',
        labelKey: 'nav.group.orders',
        items: [
            { id: 'orderSchemas', appUrlKey: 'COMPONENTS_ORDER_SCHEMAS', labelKey: 'nav.link.orderSchemas' },
            { id: 'rules', appUrlKey: 'RULES', labelKey: 'nav.link.rules' },
            { id: 'ruleSets', appUrlKey: 'RULE_SETS', labelKey: 'nav.link.ruleSets', activeOn: [ 'RULE_SET_EVALUATIONS_DETAIL_READ' ] },
            { id: 'ruleSetTester', appUrlKey: 'TESTING_RULE_SET', labelKey: 'nav.link.ruleSetTester', flag: 'web.testing.feature.enabled' },
        ],
    },
    {
        id: 'ai',
        labelKey: 'nav.group.ai',
        items: [
            { id: 'intents', appUrlKey: 'COMPONENTS_INTENTS', labelKey: 'nav.link.intents' },
            { id: 'labeling', appUrlKey: 'AI_LABELING', labelKey: 'nav.link.labeling' },
            { id: 'intentModels', appUrlKey: 'AI_INTENT_MODELS', labelKey: 'nav.link.intentModels' },
        ],
    },
    {
        id: 'integrations',
        labelKey: 'nav.group.integrations',
        items: [
            { id: 'connections', appUrlKey: 'COMPONENTS_CONNECTIONS', labelKey: 'nav.link.connections' },
            { id: 'connectionTypes', appUrlKey: 'COMPONENTS_CONNECTION_TYPES', labelKey: 'nav.link.connectionTypes' },
        ],
    },
    {
        id: 'data',
        labelKey: 'nav.group.data',
        items: [
            { id: 'constants', appUrlKey: 'CONSTANTS', labelKey: 'nav.link.constants' },
            { id: 'collections', appUrlKey: 'COLLECTIONS', labelKey: 'nav.link.collections' },
            { id: 'environments', appUrlKey: 'ENVIRONMENTS', labelKey: 'nav.link.environments', flag: 'web.environments.feature.enabled' },
            { id: 'environmentSchema', appUrlKey: 'ENVIRONMENT_SCHEMA', labelKey: 'nav.link.environmentSchema', flag: 'web.environments.feature.enabled' },
            { id: 'fileStorage', appUrlKey: 'FILE_STORAGE', labelKey: 'nav.link.fileStorage', flag: FILE_STORAGE_FLAG },
        ],
    },
    {
        id: 'reviews',
        labelKey: 'nav.group.reviews',
        items: [
            { id: 'reviewQueues', appUrlKey: 'COMPONENTS_REVIEW_QUEUES', labelKey: 'nav.link.reviewQueues' },
        ],
    },
];

export const SETUP_HUBS: AppUrlKey[] = [ 'AI', 'COMPONENTS', 'CUSTOM_DATA', 'TESTING' ];

// bottom menu, in order: BOTTOM_ITEMS, then the Wiki Pages action, then Settings last
export const BOTTOM_ITEMS: NavItem[] = [
    { id: 'labs', appUrlKey: 'LABS', labelKey: 'nav.link.labs', icon: DiscoverIcon, flag: 'web.labs.feature.enabled' },
];

export const WIKI_PAGES: NavAction = {
    id: 'wikiPages',
    labelKey: 'nav.link.wikiPages',
    icon: BookIcon,
    permissions: WIKI_PAGES_PERMISSIONS,
};

export const SETTINGS_ITEM: NavItem = { id: 'settings', appUrlKey: 'SETTINGS', labelKey: 'nav.link.settings', icon: SettingsIcon };
